/**
 * O estado da ligação, em números — o que está por trás do "anteninho".
 *
 * As barras de sinal do LiveKit respondem "está bom?" com quatro degraus
 * (excelente/boa/fraca/perdida). Chega para um relance e não chega para nada
 * mais: quando a voz corta, "conexão fraca" não diz se o problema é distância
 * (ping alto e estável), rede a perder pacotes, ou o WiFi a oscilar. O número
 * diz, e é a diferença entre "a app está lenta" e "muda de rede".
 *
 * ── O QUE SE PODE MEDIR, E O QUE NÃO ────────────────────────────────────────
 *
 * O ping de CADA UM só existe na máquina dele. Do meu lado eu meço o meu RTT
 * até ao SFU (o `getStats` do WebRTC dá-mo); do RTT dele até ao SFU não há
 * nenhum vestígio no que eu recebo. O protocolo do LiveKit até tem um campo
 * para isso (`MetricLabel.PUBLISHER_RTT`, entregue por `RoomEvent.
 * MetricsReceived`) — mas quem o publica são os SDKs de servidor e os agentes; o
 * cliente de browser recebe métricas e nunca publica nenhuma (procurar
 * `MetricsBatch` no bundle do `livekit-client` 2.22: aparece como TIPO e como
 * evento, nunca a ser enviado). Um browser a falar com outro browser, que é o
 * nosso caso, nunca receberia nada.
 *
 * Por isso cada cliente MEDE o seu e ANUNCIA-O aos outros pelo canal de dados
 * que a call já tem. É uma mensagem pequena a cada 5 s por pessoa — O(n) na
 * sala, não O(n²) de toda a gente a fazer ping a toda a gente — e o número que
 * aparece ao lado de um nome é o que aquela máquina mediu, não uma estimativa
 * nossa. Quem não anuncia (um cliente antigo, alguém que acabou de entrar) não
 * ganha número nenhum: as barras ficam como estavam. Nunca se inventa um valor
 * para preencher o espaço.
 *
 * Este ficheiro é só a LEITURA e a ESCRITA em texto — sem motor, sem React, sem
 * LiveKit. É a parte que se engana em silêncio (ler o campo errado do
 * `getStats` dá um número plausível e falso) e por isso é a parte que tem testes.
 */

/** O que sabemos da ligação de alguém num instante. `null` é "não medido" e
 *  nunca zero: zero é um valor, e um ping de 0 ms seria uma mentira redonda. */
export interface NetSample {
  /** Ida e volta até ao SFU, em milissegundos. */
  readonly rttMs: number | null;
  /** Percentagem de pacotes perdidos no caminho (0–100). */
  readonly lossPct: number | null;
  /** Quando foi medido (epoch ms) — é o que deixa distinguir um número velho. */
  readonly at: number;
}

/** De quanto em quanto tempo se mede e se anuncia. Cinco segundos são o meio
 *  termo entre um número vivo e uma mensagem a cada suspiro: o ping muda devagar
 *  e o que interessa é a ORDEM DE GRANDEZA, não o milissegundo. */
export const INTERVALO_MS = 5_000;

/** Passado isto, o número deixa de se mostrar. Três rondas: uma mensagem lossy
 *  pode perder-se, duas seguidas já é sinal de que algo está mal — e um ping de
 *  há um minuto apresentado como se fosse de agora é pior do que nenhum. */
export const VALIDADE_MS = 3 * INTERVALO_MS;

/** Uma entrada do relatório do WebRTC, com só os campos que lemos. O `getStats`
 *  devolve dezenas de tipos e a tipagem do lib.dom é `any` — declarar o que se
 *  usa é o que impede um campo mal escrito de passar despercebido. */
interface EntradaStats {
  readonly type?: string;
  /** `remote-inbound-rtp`: RTT em SEGUNDOS, medido pelos relatórios RTCP. */
  readonly roundTripTime?: number;
  /** `candidate-pair`: RTT em SEGUNDOS da última sondagem STUN. */
  readonly currentRoundTripTime?: number;
  /** `remote-inbound-rtp`: fracção 0–1 do que se perdeu no último relatório. */
  readonly fractionLost?: number;
  readonly state?: string;
  readonly nominated?: boolean;
}

/**
 * Tira o ping e a perda de um relatório do `getStats`.
 *
 * A ordem das fontes é o que decide se o número é verdade:
 *
 *  1. **`remote-inbound-rtp`** — o que o SFU nos diz de VOLTA sobre o áudio que
 *     lhe mandámos: quanto demorou a ida e volta e que fracção se perdeu. É a
 *     medida do caminho que a VOZ percorre, com a voz lá dentro.
 *  2. **`candidate-pair`** — o RTT das sondagens STUN da ligação escolhida.
 *     Serve de reserva (entra-se mudo em todas as calls desta app, e sem
 *     microfone publicado o ponto 1 não existe), mas mede o caminho, não o
 *     fluxo: por isso é o segundo e não o primeiro.
 *
 * Só se aceita o par de candidatos NOMEADO e em `succeeded`. Uma ligação tem
 * vários pares em cima da mesa — relay, refletido, local — e os que não vingaram
 * ficam no relatório com valores antigos: apanhar um deles dá um ping estável e
 * errado, que é o pior tipo de erro possível num mostrador.
 */
export function lerRede(report: ReadonlyMap<string, unknown> | undefined, agora: number): NetSample {
  let rttMs: number | null = null;
  let lossPct: number | null = null;
  let doFluxo = false;

  report?.forEach((cru) => {
    const e = cru as EntradaStats;

    if (e.type === "remote-inbound-rtp") {
      if (typeof e.roundTripTime === "number") {
        rttMs = e.roundTripTime * 1000;
        doFluxo = true;
      }
      if (typeof e.fractionLost === "number") {
        // A fracção vem 0–1 e é uma média do último relatório; negativos
        // aparecem quando chegam mais pacotes do que os anunciados (duplicados
        // de retransmissão) e não são perda nenhuma.
        lossPct = Math.max(0, e.fractionLost) * 100;
      }
      return;
    }

    if (e.type === "candidate-pair" && !doFluxo) {
      if (e.state !== "succeeded" || e.nominated !== true) return;
      if (typeof e.currentRoundTripTime === "number") rttMs = e.currentRoundTripTime * 1000;
    }
  });

  return { rttMs: rttMs === null ? null : Math.round(rttMs), lossPct, at: agora };
}

/** A amostra ainda vale? Uma que já não valha é tratada como se não existisse. */
export function fresca(sample: NetSample | undefined | null, agora: number): sample is NetSample {
  return sample !== undefined && sample !== null && agora - sample.at <= VALIDADE_MS;
}

/** Os quatro degraus do LiveKit, já em português e sem depender do enum dele —
 *  quem chama traduz uma vez, e este ficheiro fica livre do SDK. */
export type Grau = "excelente" | "boa" | "fraca" | "perdida" | "desconhecida";

const FRASE: Record<Grau, string> = {
  excelente: "Conexão excelente",
  boa: "Conexão boa",
  fraca: "Conexão fraca",
  perdida: "Conexão perdida",
  desconhecida: "Medindo a conexão",
};

/** Uma casa decimal só quando ela diz alguma coisa: "0,4%" informa, "12,0%" só
 *  ocupa espaço. */
function percentagem(valor: number): string {
  const arredondado = valor < 10 ? Math.round(valor * 10) / 10 : Math.round(valor);
  return `${String(arredondado).replace(".", ",")}%`;
}

/**
 * A frase que aparece ao passar o rato — o mostrador todo, numa linha.
 *
 * Três decisões que se vêem no texto:
 *
 *  - O GRAU vem primeiro e sozinho quando é tudo o que há. A frase antiga não
 *    desaparece por se ter passado a medir: quem só quer saber se está bom
 *    continua a ler a mesma coisa no mesmo sítio.
 *  - Um número VELHO não se mostra. Passados 15 s sem notícias, volta-se ao grau
 *    — porque um ping de há um minuto lido como se fosse de agora manda mudar de
 *    WiFi por causa de um problema que já passou.
 *  - A perda só entra quando existe. "0% de perda" a toda a hora treina o olho a
 *    ignorar a linha, e depois ninguém repara nos 4% do dia em que interessa.
 */
export function textoDaLigacao(grau: Grau, sample: NetSample | undefined | null, agora: number): string {
  if (!fresca(sample, agora)) return FRASE[grau];

  // "Medindo a conexão · ping 42 ms" era uma frase a discutir consigo própria: o
  // grau vem do SFU e demora uns segundos a chegar, o ping é medido aqui e chega
  // primeiro. Enquanto o grau não existe, diz-se o que se sabe e mais nada.
  const partes = [grau === "desconhecida" ? "Conexão" : FRASE[grau]];
  if (sample.rttMs !== null) partes.push(`ping ${sample.rttMs} ms`);
  // Meio ponto percentual é onde a voz começa a picar; abaixo disso é ruído de
  // medição e mostrá-lo era dar um alarme por cada arredondamento.
  if (sample.lossPct !== null && sample.lossPct >= 0.5) partes.push(`${percentagem(sample.lossPct)} de perda`);
  return partes.join(" · ");
}
